import type { SecondaryDimensionValues as CoreSecondaryDimensionValues } from '@revelio/core';
import type {
  CompositionV2EntRespData,
  TransitionsBreakdowns,
} from '@revelio/data-access';

export type TopNDimensionValues = 'top_geography' | 'top_competitors';

// Import base type from core and re-export for backward compatibility
export type SecondaryDimensionValues = CoreSecondaryDimensionValues;

export type CompositionSecondaryDimensionValues = keyof Pick<
  CompositionV2EntRespData,
  | 'rics'
  | 'geography'
  | 'role'
  | 'skills'
  | 'gender'
  | 'ethnicity'
  | 'education'
  | 'seniority'
>;

const compositionSecondaryDimension: Record<
  CompositionSecondaryDimensionValues,
  boolean
> = {
  rics: true,
  geography: true,
  role: true,
  skills: true,
  gender: true,
  ethnicity: true,
  education: true,
  seniority: true,
};
export const isCompositionSecondaryDimension = (
  segment: SecondaryDimensionValues
): segment is CompositionSecondaryDimensionValues =>
  compositionSecondaryDimension[segment as CompositionSecondaryDimensionValues];

export const compositionSecondaryDimensionSet: ReadonlySet<string> = new Set(
  Object.keys(compositionSecondaryDimension)
);

export type TransitionSecondaryDimensionValues = keyof Pick<
  TransitionsBreakdowns,
  | 'role'
  | 'rics'
  | 'geography'
  | 'seniority'
  | 'gender'
  | 'ethnicity'
  | 'education'
>;

const transitionSecondaryDimension: Record<
  TransitionSecondaryDimensionValues,
  boolean
> = {
  role: true,
  rics: true,
  geography: true,
  gender: true,
  ethnicity: true,
  education: true,
  seniority: true,
};
export const isTransitionSecondaryDimension = (
  segment: SecondaryDimensionValues
): segment is TransitionSecondaryDimensionValues =>
  transitionSecondaryDimension[segment as TransitionSecondaryDimensionValues];

export type PostingSecondaryDimension = 'role' | 'company';

const postingSecondaryDimension: Record<PostingSecondaryDimension, boolean> = {
  role: true,
  company: true,
};
export const isPostingSecondaryDimension = (
  segment: SecondaryDimensionValues
): segment is PostingSecondaryDimension =>
  postingSecondaryDimension[segment as PostingSecondaryDimension];

export type CompensationSecondaryDimension = 'role' | 'company' | 'geography';

const compensationSecondaryDimension: Record<
  CompensationSecondaryDimension,
  boolean
> = {
  role: true,
  company: true,
  geography: true,
};
export const isCompensationSecondaryDimension = (
  segment: SecondaryDimensionValues
): segment is CompensationSecondaryDimension =>
  compensationSecondaryDimension[segment as CompensationSecondaryDimension];

export type SecondaryDimensionOption = {
  value: SecondaryDimensionValues;
  label: string;
};

export const secondaryDimensionOptions: SecondaryDimensionOption[] = [
  { value: 'timeseries', label: 'Timeseries' },
  { value: 'competitors', label: 'Competitors' },
  { value: 'rics', label: 'Industry' },
  { value: 'geography', label: 'Geography' },
  { value: 'role', label: 'Role' },
  { value: 'company', label: 'Company' },
  { value: 'skills', label: 'Skill' },
  { value: 'gender', label: 'Gender' },
  { value: 'ethnicity', label: 'Ethnicity' },
  { value: 'education', label: 'Education' },
  { value: 'seniority', label: 'Seniority' },
];
